import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Popconfirm, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { productApi } from "api/product.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { ModalStatus } from "types/modal";
import { QueryParam } from "types/query";
import { Product } from "types/product";
import { $url } from "utils/url";
import { formatVND, getTitle } from "utils";
import { formatDate } from "utils/date";
import {
  ActionButton,
  INA,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { ProductModal } from "./components/ProductModal";

export const ProductPage = ({ title = "" }) => {
  const [searchValue, setSearchValue] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 10,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Partial<Product>>({});
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 })),
      300,
    ),
    [],
  );

  useEffect(() => {
    document.title = getTitle(title);
  }, []);

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    setLoading(true);
    const res = await productApi.findAll(query);
    setLoading(false);
    setData(res.data.products);
    setTotal(res.data.total);
  };

  const handleDelete = async (row: Product) => {
    setLoading(true);
    try {
      await productApi.delete(row.id);
      message.success("Xóa thành công");
      fetchData();
    } finally {
      setLoading(false);
    }
  };

  const columns: TableColumnsType<Product> = [
    {
      title: "STT",
      key: "stt",
      width: 70,
      render: (_, __, index) => (
        <span>{(query.page - 1) * query.limit + index + 1}</span>
      ),
    },
    {
      title: "Tên sản phẩm",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <INA
          image={record.image ? $url(record.image) : undefined}
          name={record.name}
          additional={record.productCategory?.name || "--"}
        />
      ),
    },
    {
      title: "Đã bán",
      dataIndex: "sold",
      key: "sold",
      width: 100,
      render: (text) => <span>{formatVND(text)}</span>,
    },
    {
      title: "Tình trạng kho",
      dataIndex: "isOutOfStock",
      key: "isOutOfStock",
      width: 130,
      render: (_, record) => (
        <span>{record.isOutOfStock ? "Hết hàng" : "Còn hàng"}</span>
      ),
    },
    {
      title: "Giá gốc",
      dataIndex: "originPrice",
      key: "originPrice",
      width: 130,
      render: (text) => <span>{formatVND(text)}</span>,
    },
    {
      title: "Giá KM",
      dataIndex: "finalPrice",
      key: "finalPrice",
      width: 130,
      render: (text) => <span>{formatVND(text)}</span>,
    },
    {
      title: "Ngày đăng",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (_, record) => <span>{formatDate(record.createdAt)}</span>,
    },
    {
      title: "Thao tác",
      key: "action",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div className="flex gap-2 justify-center items-center">
          <Tooltip title="Cập nhật">
            <ActionButton
              aria-label="Cập nhật"
              onClick={() => {
                setSelectedProduct(record);
                setVisibleModal(true);
                setModalStatus("update");
              }}
            >
              <EditOutlined style={{ color: "#6c84e5" }} />
            </ActionButton>
          </Tooltip>
          <Tooltip title="Xóa">
            <Popconfirm
              onConfirm={() => handleDelete(record)}
              placement="top"
              title="Xác nhận xóa"
            >
              <ActionButton aria-label="Xóa">
                <DeleteOutlined style={{ color: "#ef4444" }} />
              </ActionButton>
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: searchValue,
              placeholder: "Tìm kiếm",
              onChange: (value) => {
                setSearchValue(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearchValue("");
                        debounceSearch.cancel();
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            leftActions={
              <AppButton
                tone="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setVisibleModal(true);
                  setModalStatus("create");
                  setSelectedProduct({});
                }}
              >
                Thêm mới
              </AppButton>
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<Product>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            scroll={{ x: 900 }}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              query.page = page;
              query.limit = limit;
              setQuery({ ...query });
            }}
          />
        }
      />

      <ProductModal
        onSubmitOk={fetchData}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        product={selectedProduct}
        status={modalStatus}
      />
    </div>
  );
};
